import { Reducer } from 'redux';
import { createSelector } from 'reselect';
import { omit } from 'lodash';
import { all, put, select, takeEvery } from 'redux-saga/effects';
import { Store } from '../../../store/store.model';
import { history } from '../../../store/history';

// org-tabs tracks organizations that have been opened in the header, mirroring how
// `locations`/`currentLocationId` track location tabs. Each entry only needs the org's
// id and name to render a tab; full org details live on the org details page itself.
export interface OrgTabModel {
  OrgId: string;
  Name: string;
}

export type OrgTabsState = Record<string, OrgTabModel>;
export type CurrentOrgIdState = string | null;

export enum OrgTabsActionTypes {
  AddOrgTab = 'ADD_ORG_TAB',
  RemoveOrgTab = 'REMOVE_ORG_TAB',
  SetCurrentOrgId = 'SET_CURRENT_ORG_ID',
  UnsetCurrentOrgId = 'UNSET_CURRENT_ORG_ID',
}

export const addOrgTab = (org: OrgTabModel) => ({
  type: OrgTabsActionTypes.AddOrgTab,
  payload: org,
});

export const removeOrgTab = (orgId: string) => ({
  type: OrgTabsActionTypes.RemoveOrgTab,
  payload: orgId,
});

export const setCurrentOrgId = (orgId: string) => ({
  type: OrgTabsActionTypes.SetCurrentOrgId,
  payload: orgId,
});

export const unsetCurrentOrgId = () => ({
  type: OrgTabsActionTypes.UnsetCurrentOrgId,
});

export const orgTabsReducer: Reducer<OrgTabsState, any> = (state = {}, action) => {
  switch (action.type) {
    case OrgTabsActionTypes.AddOrgTab:
      return { ...state, [action.payload.OrgId]: action.payload };
    case OrgTabsActionTypes.RemoveOrgTab:
      return omit(state, action.payload);
    default:
      return state;
  }
};

export const currentOrgIdReducer: Reducer<CurrentOrgIdState, any> = (
  state = null,
  action
) => {
  switch (action.type) {
    case OrgTabsActionTypes.SetCurrentOrgId:
      return action.payload;
    case OrgTabsActionTypes.UnsetCurrentOrgId:
      return null;
    default:
      return state;
  }
};

export const selectOrgTabs = (state: Store) => state.orgTabs ?? {};
export const selectOrgTabsArray = createSelector(selectOrgTabs, (orgTabs) =>
  Object.values(orgTabs)
);
export const selectCurrentOrgId = (state: Store) => state.currentOrgId ?? null;
export const selectCurrentOrgTab = createSelector(
  selectOrgTabs,
  selectCurrentOrgId,
  (orgTabs, currentOrgId) => (currentOrgId ? orgTabs[currentOrgId] ?? null : null)
);

// Mirrors handleRemoveLocation in locations.action.ts: closing the tab for the
// organization currently being viewed sends the user back to the default search page,
// same as closing the currently active location tab.
export const handleRemoveOrgTab = function* (action: ReturnType<typeof removeOrgTab>) {
  const currentOrgId: ReturnType<typeof selectCurrentOrgId> = yield select(
    selectCurrentOrgId
  );

  if (currentOrgId === action.payload) {
    yield put(unsetCurrentOrgId());
    history.push('/');
  }
};

export const orgTabsSaga = function* () {
  yield all([takeEvery(OrgTabsActionTypes.RemoveOrgTab, handleRemoveOrgTab)]);
};
